import { useQuery } from '@tanstack/react-query';
import { Archive, Ban, Clock, FileText, Landmark, ShieldCheck } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { CargandoEnLinea } from '@/shared/ui/Cargando';
import { PanelLateral } from '@/shared/ui/PanelLateral';
import { colorDelEstado, colorDeLaSituacion } from './estadosDeFactura';
import type { FacturaListada } from './estadosDeFactura';

interface Linea {
  cuenta: string | null;
  direccion: string | null;
  concepto: string;
  valor: number;
  descuento: number;
}

interface FacturaConDetalle {
  factura: FacturaListada;
  lineas: Linea[];
  numeroAutorizacion: string | null;

  fechaAutorizacion: string | null;
  mensajeSri: string | null;
  tieneXml: boolean;
  motivoAnulacion: string | null;
}

export function PanelDeLaFactura({
  factura,
  abierto,
  alCerrar,
}: {
  factura: FacturaListada;
  abierto: boolean;
  alCerrar: () => void;
}) {
  const detalle = useQuery({
    queryKey: ['factura', factura.id, factura.historica],
    queryFn: () =>
      api.get<FacturaConDetalle>(`/facturas/${factura.id}`, {
        query: { historica: factura.historica },
      }),
    enabled: abierto,
  });

  const d = detalle.data;

  return (
    <PanelLateral
      abierto={abierto}
      alCerrar={alCerrar}
      titulo={factura.numero ?? 'Factura sin numerar'}
      descripcion={`${factura.cliente} · ${factura.periodo}`}
      icono={FileText}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase ${colorDelEstado(factura.estado)}`}
          >
            {factura.estado}
          </span>
          <span
            className={`text-[10px] font-bold uppercase ${colorDeLaSituacion(factura.situacionSri)}`}
          >
            Ante el SRI: {factura.situacionSri.replace(/_/g, ' ')}
          </span>
          {factura.historica && (
            <span className="inline-flex items-center gap-1 rounded bg-armazon-100 px-1.5 py-0.5 text-[10px] uppercase text-armazon-500">
              <Archive className="size-3" aria-hidden />
              Del sistema anterior
            </span>
          )}
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-xl border border-armazon-200 bg-armazon-50/50 p-4">
          <Dato rotulo="Identificación" valor={factura.identificacion} />
          <Dato rotulo="Período" valor={factura.periodo} />
          <Dato rotulo="Emitida" valor={enFecha(factura.fechaEmision)} />
          <Dato rotulo="Vence" valor={enFecha(factura.vencimiento)} />
        </dl>

        {factura.claveAcceso && (
          <div className="rounded-xl border border-armazon-200 p-4">
            <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-armazon-500">
              <ShieldCheck className="size-3.5" aria-hidden />
              Clave de acceso
            </p>
            <p className="importe mt-1 break-all text-[11px] text-armazon-700">
              {factura.claveAcceso}
            </p>

            {d?.fechaAutorizacion && (
              <p className="mt-3 flex items-center gap-2 text-[11px] uppercase text-marca-700">
                <Clock className="size-3.5 shrink-0" aria-hidden />
                <span>
                  Autorizada el <b className="importe">{d.fechaAutorizacion}</b>
                </span>
              </p>
            )}

            {d?.numeroAutorizacion && d.numeroAutorizacion !== factura.claveAcceso && (
              <p className="importe mt-2 break-all text-[11px] text-armazon-500">
                Autorización: {d.numeroAutorizacion}
              </p>
            )}

            {d?.mensajeSri && !d.fechaAutorizacion && (
              <p className="mt-3 text-[11px] uppercase leading-relaxed text-amber-700">
                {d.mensajeSri}
              </p>
            )}
          </div>
        )}

        {d?.motivoAnulacion && (
          <p className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-4 text-xs text-red-900">
            <Ban className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>
              <b className="uppercase">Anulada:</b> {d.motivoAnulacion}
            </span>
          </p>
        )}

        <div>
          <p className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-armazon-600">
            <Landmark className="size-3.5" aria-hidden />
            Qué se cobra
          </p>

          {detalle.isPending ? (
            <CargandoEnLinea detalle="Abriendo la factura" />
          ) : d && d.lineas.length > 0 ? (
            <ul className="divide-y divide-armazon-100 rounded-xl border border-armazon-200">
              {d.lineas.map((l, i) => (
                <li key={i} className="flex items-start gap-3 px-4 py-2.5">
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-semibold uppercase text-armazon-800">
                      {l.concepto}
                    </span>
                    <span className="block text-[10px] uppercase text-armazon-400">
                      {l.cuenta ? `Cuenta ${l.cuenta}` : 'Sin cuenta'}
                      {l.direccion ? ` · ${l.direccion}` : ''}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="importe block text-xs font-bold text-armazon-900">
                      ${l.valor.toFixed(2)}
                    </span>
                    {l.descuento > 0 && (
                      <span className="importe block text-[10px] text-marca-700">
                        −${l.descuento.toFixed(2)}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          ) : (

            <p className="rounded-xl border border-armazon-200 bg-armazon-50 p-4 text-[11px] uppercase leading-relaxed text-armazon-500">
              {factura.historica
                ? 'El sistema anterior guardó el total de esta factura, no su desglose.'
                : 'Esta factura no tiene detalle registrado.'}
            </p>
          )}
        </div>

        <dl className="space-y-1.5 rounded-xl border border-armazon-200 p-4">
          <Total rotulo="Subtotal" valor={factura.subtotal} />
          {factura.descuento > 0 && (
            <Total rotulo="Descuento" valor={-factura.descuento} tono="descuento" />
          )}
          <Total rotulo="Total" valor={factura.total} grande />
        </dl>
      </div>
    </PanelLateral>
  );
}

function Dato({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <dt className="text-[10px] font-semibold uppercase tracking-wide text-armazon-500">
        {rotulo}
      </dt>
      <dd className="importe text-xs font-semibold text-armazon-800">{valor}</dd>
    </div>
  );
}

function Total({
  rotulo,
  valor,
  grande,
  tono,
}: {
  rotulo: string;
  valor: number;
  grande?: boolean;
  tono?: 'descuento';
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt
        className={`text-[11px] uppercase ${grande ? 'font-bold text-armazon-700' : 'text-armazon-500'}`}
      >
        {rotulo}
      </dt>
      <dd
        className={`importe font-bold ${
          grande ? 'text-lg text-armazon-900' : 'text-xs'
        } ${tono === 'descuento' ? 'text-marca-700' : ''}`}
      >
        ${Math.abs(valor).toFixed(2)}
      </dd>
    </div>
  );
}

function enFecha(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${anio}`;
}
